import { z } from 'zod';
import { factSchema } from './schemas.js';
/**
 * The wire format for chat streaming. Both the widget and the console consume
 * this, so the union lives here rather than being re-declared per client.
 *
 * Each SSE frame is `data: <json>\n\n` where <json> matches `chatStreamEvent`.
 */
export const chatStreamEventSchema = z.discriminatedUnion('type', [
    /** Emitted once, before any text, so clients can persist ids immediately. */
    z.object({
        type: z.literal('start'),
        conversationId: z.uuid(),
    }),
    /** Incremental assistant text. */
    z.object({
        type: z.literal('delta'),
        text: z.string(),
    }),
    /** A fact the trainee bot just wrote to the notebook (training channel). */
    z.object({
        type: z.literal('fact'),
        action: z.enum(['created', 'updated', 'archived']),
        fact: factSchema,
    }),
    /** The bot could not answer and logged the question for the owner. */
    z.object({
        type: z.literal('gap'),
        question: z.string(),
    }),
    /** The bot captured the visitor's contact details. */
    z.object({
        type: z.literal('message_taken'),
        contact: z.string(),
    }),
    /**
     * The bot is about to consult the tenant's backend about the signed-in
     * visitor's own account. Emitted before the call, so the widget can say
     * "checking your account…" while it happens; it carries no account data and
     * no outcome. What actually happened is in the audit trail the console reads.
     */
    z.object({
        type: z.literal('account_lookup'),
        action: z.string(),
    }),
    /** Terminal success frame. */
    z.object({
        type: z.literal('done'),
        conversationId: z.uuid(),
    }),
    /** Terminal failure frame. `code` is stable; `message` is human-facing. */
    z.object({
        type: z.literal('error'),
        code: z.string(),
        message: z.string(),
    }),
]);
/** Serialise one event as an SSE frame. */
export function encodeSseEvent(event) {
    return `data: ${JSON.stringify(event)}\n\n`;
}
/**
 * Incremental SSE parser. Feed it raw chunks; it returns whichever complete
 * events are now available and buffers the remainder.
 */
export function createSseParser() {
    let buffer = '';
    return (chunk) => {
        buffer += chunk;
        const events = [];
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            for (const line of frame.split('\n')) {
                if (!line.startsWith('data:'))
                    continue;
                const payload = line.slice(5).trim();
                if (!payload)
                    continue;
                try {
                    const parsed = chatStreamEventSchema.safeParse(JSON.parse(payload));
                    if (parsed.success)
                        events.push(parsed.data);
                }
                catch {
                    // Ignore malformed frames rather than tearing down the stream.
                }
            }
            boundary = buffer.indexOf('\n\n');
        }
        return events;
    };
}
//# sourceMappingURL=sse.js.map